<script setup lang="ts">
  import { computed, watch } from 'vue';
  import {
    DefaultTable,
    PaginateOrScroll,
    SearchNoResults,
    SelectInput,
    TableHeaderSection,
  } from '@/shared/ui';
  import { usePermission } from '@/entities/Permission';
  import type {
    TrainingMatrixMetadata,
    TrainingMatrixOrder,
    TrainingMatrixPositionRow,
    TrainingMatrixRevisionInfo,
    TrainingMatrixSort,
  } from '@/entities/Training';
  import {
    ALL_ITEMS_OPTION_ID,
    MATRIX_QUANTITY_OPTIONS,
    TRAINING_MATRIX_ORDER_OPTIONS,
  } from '../model/useTrainingMatrixTable';
  import type { useTrainingMatrixSelection } from '../model/useTrainingMatrixSelection';
  import type { useEditTrainingMatrixRevision } from '../model/useEditTrainingMatrixRevision';
  import { buildMatrixRows } from '../lib/buildMatrixRows';
  import TrainingMatrixTableRow from './TrainingMatrixTableRow.vue';
  import TrainingMatrixRevisionControl from './TrainingMatrixRevisionControl.vue';

  const TABLE_HEADERS = ['Cargos', 'Prefixo', 'Título', 'Ações'];

  interface Props {
    positions: TrainingMatrixPositionRow[];
    metadata: TrainingMatrixMetadata | null;
    revision: TrainingMatrixRevisionInfo | null;
    loading: boolean;
    error: string | null;
    page: number;
    itemsPerPage: number;
    sort: TrainingMatrixSort | '';
    order: TrainingMatrixOrder | '';
    isFilterActive: boolean;
    selection: ReturnType<typeof useTrainingMatrixSelection>;
    revisionEdit: ReturnType<typeof useEditTrainingMatrixRevision>;
    allPositions: TrainingMatrixPositionRow[];
    hasMorePositions: boolean;
    loadingMorePositions: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'change-page': [page: number];
    'change-items-per-page': [itemsPerPage: number];
    'update:sort': [sortCamp: TrainingMatrixSort | '', orderFlow: TrainingMatrixOrder | ''];
    'filter-clicked': [];
    'edit-position': [positionId: number];
    'delete-position': [positionId: number];
    'edit-selected-clicked': [];
    'delete-selected-clicked': [];
  }>();

  const { can } = usePermission();
  const canManageMatrix = computed(() => can('readMatrix', 'Training').value);

  const activeItems = computed(() =>
    props.itemsPerPage === ALL_ITEMS_OPTION_ID ? props.allPositions : props.positions
  );
  const rows = computed(() => buildMatrixRows(activeItems.value));
  const visiblePositionIds = computed(() => activeItems.value.map((position) => position.id));

  watch(activeItems, (items) => props.selection.syncDocuments(items), { immediate: true });
</script>

<template>
  <div data-testid="training-matrix-table">
    <div
      v-if="error"
      data-testid="training-matrix-error-state"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ error }}
    </div>

    <div
      v-else-if="loading && !metadata"
      data-testid="training-matrix-loading-state"
      class="flex items-center justify-center py-12"
    >
      <span class="text-sm text-gray-05">Carregando matriz de treinamentos...</span>
    </div>

    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-3 pb-4">
        <TrainingMatrixRevisionControl
          :revision="revision"
          :can-edit="canManageMatrix"
          :edit="revisionEdit"
        />

        <SelectInput
          :model-value="itemsPerPage"
          :options="MATRIX_QUANTITY_OPTIONS"
          container-class="w-auto"
          arrow-class="w-5 h-5"
          shadow-class="shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          @update:model-value="(value) => emit('change-items-per-page', Number(value))"
        />
      </div>

      <PaginateOrScroll
        :items="positions"
        :all-items="allPositions"
        :has-more-items="hasMorePositions"
        :loading-more="loadingMorePositions"
        :metadata="metadata"
        :selected-quantity="itemsPerPage"
        entity-name="cargo"
        @page-changed="(newPage) => emit('change-page', newPage)"
      >
        <template #table>
          <DefaultTable>
            <TableHeaderSection
              title="Matriz de Treinamentos"
              :headers="TABLE_HEADERS"
              :show-column-headers="activeItems.length > 0"
              :has-selected="selection.hasSelected.value"
              :selected-count="selection.selectedCount.value"
              :selected-documents="[]"
              :all-selected="selection.isAllSelected(visiblePositionIds)"
              :item-count="activeItems.length"
              item-type="cargos"
              :order-options="TRAINING_MATRIX_ORDER_OPTIONS"
              :current-sort="sort"
              :current-order="order"
              :has-filter="true"
              :is-filter-active="isFilterActive"
              th-checkbox-class="w-12 align-middle text-center"
              th-class="text-left text-sm font-semibold text-gray-07 px-4 py-4.5 whitespace-nowrap align-middle"
              :has-delete="canManageMatrix"
              :delete-disabled="!selection.hasSelectedWithDocuments.value"
              :has-edit="canManageMatrix"
              :edit-disabled="false"
              @toggle-all="selection.toggleAll(visiblePositionIds)"
              @update:sort="
                (sortCamp, orderFlow) =>
                  emit('update:sort', sortCamp as TrainingMatrixSort | '', orderFlow as TrainingMatrixOrder | '')
              "
              @filter-clicked="emit('filter-clicked')"
              @edit="emit('edit-selected-clicked')"
              @delete="emit('delete-selected-clicked')"
            />
            <tbody class="border-t border-gray-02">
              <tr v-if="activeItems.length === 0">
                <td
                  :colspan="TABLE_HEADERS.length + 1"
                  class="py-8"
                >
                  <SearchNoResults data-testid="training-matrix-empty-state" />
                </td>
              </tr>
              <template v-else>
                <TrainingMatrixTableRow
                  v-for="row in rows"
                  :key="row.key"
                  :row="row"
                  :is-selected="selection.isSelected(row.positionId)"
                  @toggle-selection="selection.toggle"
                  @edit-clicked="emit('edit-position', $event)"
                  @delete-clicked="emit('delete-position', $event)"
                />
              </template>
            </tbody>
          </DefaultTable>
        </template>
      </PaginateOrScroll>
    </template>
  </div>
</template>
